import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useState } from "react";
import {
  Control,
  FieldValues,
  useController,
  useFormContext,
  useWatch
} from "react-hook-form";

import AddIcon from "@mui/icons-material/Add";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import type { SxProps } from "@mui/material/styles";

import { useDebounce } from "hooks";

import PatientFieldWithSearch from "components/patient-field-with-search";
import PhysicianField from "components/physician-field";
import SubReferralSourceShortForm from "components/sub-referral-source-short-form";
import { EXTENDED_SUB_REFERRAL_SOURCE_FEATURE } from "constants/feature-flags";
import { PATIENT_TYPE, PHYSICIAN_TYPE } from "constants/referral_sources";
import useSubReferralSourceOptions from "features/patients/use-sub-referral-source-options.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import { useGetReferralSourceChildrenQuery } from "services/referral-sources";
import {
  ReferralSource,
  SubReferralSource
} from "services/types/referral-source.type";

interface ReferralSourceFields {
  [key: string]: unknown;
  referral_source: number | ReferralSource | "" | null;
  sub_referral_source:
    | number
    | SubReferralSource
    | string
    | ""
    | null
    | undefined;
  sub_referral_source_object?: any;
}

type SubReferralSourceFieldProps = {
  control: Control<any, object>;
  sx?: SxProps;
  disabled?: boolean;
  required?: boolean;
  dropDownZIndex?: number;
};

type SubReferralSourceOption = {
  value: string;
  label: string;
};

const PAGE_SIZE = 25;

export default function SubReferralSourceField({
  control,
  sx,
  disabled = false,
  required = false,
  dropDownZIndex
}: SubReferralSourceFieldProps) {
  const flags = useFlags();
  const isExtendedSubReferralSourceEnabled =
    flags[EXTENDED_SUB_REFERRAL_SOURCE_FEATURE];
  const { setValue, clearErrors, trigger } =
    useFormContext<ReferralSourceFields>();
  const [referralSource, subReferralSource] = useWatch<
    FieldValues,
    ["referral_source", "sub_referral_source"]
  >({
    control,
    name: ["referral_source", "sub_referral_source"]
  });

  const {
    subReferralSourceOptions,
    referredType,
    currentSubReferralSourceObject
  } = useSubReferralSourceOptions(
    Number(referralSource),
    Number(subReferralSource)
  );

  const [isOpenForm, setFormIsOpen] = useState<boolean>(false);
  const { isAdminOrBusinessOwner } = useUserRoles();

  // Search and pagination state
  const [searchText, setSearchText] = useState("");
  const { value: debouncedSearch, isDebouncing } = useDebounce(searchText);
  const [limit, setLimit] = useState(PAGE_SIZE);

  // Reset search and pagination when parent referral source changes
  useEffect(() => {
    setSearchText("");
    setLimit(PAGE_SIZE);
  }, [referralSource]);

  // Reset pagination when search changes
  useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [debouncedSearch]);

  // Fetch paginated children with search support
  const { data: childrenData, isFetching: isChildrenFetching } =
    useGetReferralSourceChildrenQuery(
      {
        referralSourceId: Number(referralSource),
        search: debouncedSearch || undefined,
        limit,
        is_active: true
      },
      {
        skip:
          !referralSource ||
          isNaN(Number(referralSource)) ||
          (isExtendedSubReferralSourceEnabled &&
            (referredType === PATIENT_TYPE || referredType === PHYSICIAN_TYPE))
      }
    );

  // Build options from paginated results
  const paginatedOptions = useMemo<SubReferralSourceOption[]>(
    () =>
      (childrenData?.results ?? []).map(({ id, name }) => ({
        value: String(id),
        label: name
      })),
    [childrenData]
  );

  const hasMore =
    (childrenData?.count ?? 0) > (childrenData?.results?.length ?? 0);
  const remainingCount =
    (childrenData?.count ?? 0) - (childrenData?.results?.length ?? 0);

  // Find selected option for Autocomplete display
  const selectedOption = useMemo<SubReferralSourceOption | null>(() => {
    if (!subReferralSource) return null;
    const found = paginatedOptions.find(
      (opt) => opt.value === String(subReferralSource)
    );
    if (found) return found;
    // Fallback: use the individually-fetched sub referral source object name
    if (currentSubReferralSourceObject?.name) {
      return {
        value: String(subReferralSource),
        label: currentSubReferralSourceObject.name
      };
    }
    return null;
  }, [subReferralSource, paginatedOptions, currentSubReferralSourceObject]);

  // Use paginated count when available, fall back to inline children for has-children check
  const hasChildren =
    (childrenData?.count ?? subReferralSourceOptions.length) > 0;

  useEffect(() => {
    if (currentSubReferralSourceObject) {
      setValue("sub_referral_source_object", currentSubReferralSourceObject, {
        shouldValidate: true,
        shouldDirty: true
      });
    } else {
      setValue("sub_referral_source_object", null, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  }, [setValue, currentSubReferralSourceObject]);

  const isSubReferralFieldRequired = useMemo(
    () => (!referredType && !hasChildren ? false : required),
    [referredType, required, hasChildren]
  );

  // Register the field for validation via useController
  const {
    fieldState: { error: fieldError }
  } = useController({
    name: "sub_referral_source" as any,
    control,
    rules: {
      required: {
        value: isSubReferralFieldRequired,
        message: "Sub Referral Source is required"
      }
    },
    shouldUnregister: true
  });

  useEffect(() => {
    if (required) {
      const newRequired = hasChildren || referredType;
      setValue("sub_referral_source_required", newRequired, {
        shouldValidate: false,
        shouldDirty: true
      });

      if (!newRequired) {
        clearErrors("sub_referral_source");
      }

      // Trigger validation to update form's validity state
      setTimeout(() => trigger("sub_referral_source"), 0);
    }
  }, [setValue, hasChildren, required, clearErrors, trigger, referredType]);

  if (isExtendedSubReferralSourceEnabled) {
    if (referredType === PATIENT_TYPE) {
      return (
        <PatientFieldWithSearch
          control={control}
          fieldName="sub_referral_source_object"
          label="Sub Referral Source"
          placeholder="Search Patient"
          transformOption={(option) => ({
            ...option,
            content_type: PATIENT_TYPE
          })}
          onOptionSelected={() => {
            setValue("sub_referral_source", "", { shouldValidate: true });
          }}
          hideAvatar
          sx={sx}
          disabled={disabled}
          required={isSubReferralFieldRequired}
        />
      );
    }
    if (referredType === PHYSICIAN_TYPE) {
      return (
        <PhysicianField
          name="sub_referral_source_object"
          label="Sub Referral Source"
          control={control}
          transformOption={(option) => ({
            ...option,
            content_type: PHYSICIAN_TYPE
          })}
          sx={sx}
          disabled={disabled}
          allowNew
          dropDownZIndex={dropDownZIndex}
          required={isSubReferralFieldRequired}
          afterUpdate={() => {
            setValue("sub_referral_source", "", { shouldValidate: true });
          }}
        />
      );
    }
  }

  if (
    !referralSource ||
    (!isAdminOrBusinessOwner && !referredType && !hasChildren)
  )
    return null;

  return (
    <>
      {/* Label + "Add" button as an external header, so the "+" sits next to the
          label text — matching the Referral Source field above (SelectFormField). */}
      <Box display="flex" alignItems="center" gap="4px">
        <InputLabel
          disabled={disabled}
          required={isSubReferralFieldRequired}
          shrink
        >
          Sub Referral Source
        </InputLabel>
        {isAdminOrBusinessOwner && (
          <Box sx={{ pb: 0.5 }}>
            <Tooltip title="Add new sub referral source">
              <IconButton
                size="small"
                onClick={() => {
                  setFormIsOpen(true);
                }}
                color="success"
                sx={{ padding: 0, border: "1px solid #ccc" }}
              >
                <AddIcon color="success" fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      <Box display="flex" alignItems="flex-start">
        <Autocomplete<SubReferralSourceOption, false, false, false>
          fullWidth
          options={paginatedOptions}
          value={selectedOption}
          loading={isChildrenFetching || isDebouncing}
          disabled={disabled}
          openOnFocus
          slotProps={
            dropDownZIndex
              ? { popper: { sx: { zIndex: dropDownZIndex } } }
              : undefined
          }
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          filterOptions={(x) => x}
          noOptionsText={
            isChildrenFetching || isDebouncing
              ? "Loading..."
              : "No sub referral sources found"
          }
          onInputChange={(_, value, reason) => {
            if (reason === "input") setSearchText(value);
          }}
          onChange={(_, option) => {
            setValue("sub_referral_source", option?.value ?? null, {
              shouldValidate: true,
              shouldDirty: true
            });
            setSearchText("");
          }}
          PaperComponent={(paperProps) => (
            <Paper {...paperProps}>
              {paperProps.children}
              {hasMore && (
                <Box
                  onMouseDown={(e) => e.preventDefault()}
                  sx={{
                    p: 1,
                    textAlign: "center",
                    borderTop: "1px solid",
                    borderColor: "divider"
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => setLimit((prev) => prev + PAGE_SIZE)}
                    disabled={isChildrenFetching}
                  >
                    {isChildrenFetching
                      ? "Loading..."
                      : `Show more (${remainingCount} remaining)`}
                  </Button>
                </Box>
              )}
            </Paper>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              // Autocomplete's renderInput params carry no `name`, which leaves the
              // field unaddressable from outside — see the same fix in
              // patient-field.component.tsx (DEV-2995).
              name="sub_referral_source"
              required={isSubReferralFieldRequired}
              error={Boolean(fieldError)}
              helperText={fieldError?.message || ""}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isChildrenFetching || isDebouncing ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          sx={sx}
        />
      </Box>

      <SubReferralSourceShortForm
        isOpen={isOpenForm}
        onClose={() => setFormIsOpen(false)}
        onAdd={(id) => {
          setValue("sub_referral_source", id.toString());
        }}
        parentReferralSourceId={Number(referralSource)}
        zIndex={dropDownZIndex}
      />
    </>
  );
}
